import { hasTeamRole, type Studio } from '@greenroom/shared'
import { useQuery } from '@tanstack/react-query'
import { Clapperboard, Plus, Users, Video } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { CreateStudioDialog } from '../components/studio-dialogs'
import { Button, LinkButton } from '../components/ui/button'
import { Badge, EmptyState, PageHeader } from '../components/ui/misc'
import { FullPageSpinner } from '../components/ui/spinner'
import { useCurrentTeam } from '../lib/auth'
import { formatRelative, plural } from '../lib/format'
import { q } from '../lib/queries'
import { useTitle } from '../lib/title'
import { QueryError } from './errors'

export function StudiosPage() {
  const { team } = useCurrentTeam()
  useTitle('Studios', team.name)
  const studios = useQuery(q.studios(team.id))
  const [creating, setCreating] = useState(false)
  const canCreate = hasTeamRole(team.role, 'producer')

  const newButton = canCreate && (
    <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
      New studio
    </Button>
  )
  return (
    <>
      <PageHeader title="Studios" subtitle="Rooms where you meet guests and record." actions={newButton} />
      {studios.isPending ? (
        <FullPageSpinner />
      ) : studios.isError ? (
        <QueryError error={studios.error} retry={studios.refetch} what="list" />
      ) : studios.data.length === 0 ? (
        <EmptyState icon={<Clapperboard />} title="No studios yet" action={newButton}>
          {canCreate ? 'Create a studio, send the guest link and press record.' : 'A producer of this team has to create a studio first.'}
        </EmptyState>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {studios.data.map((s) => (
            <StudioCard key={s.id} studio={s} />
          ))}
        </div>
      )}
      <CreateStudioDialog teamId={team.id} open={creating} onOpenChange={setCreating} />
    </>
  )
}

function StudioCard({ studio }: { studio: Studio }) {
  return (
    <div className="card relative flex flex-col p-5 transition-colors hover:border-border-strong">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex size-10 items-center justify-center rounded-lg bg-accent-soft text-accent">
          <Video className="size-5" />
        </div>
        <div className="flex gap-1.5">
          {studio.live.recording && (
            <Badge tone="rec">
              <span className="size-1.5 animate-pulse-rec rounded-full bg-rec" />
              Recording
            </Badge>
          )}
          {studio.live.participants > 0 && (
            <Badge tone="ok">
              <Users className="size-3" />
              {studio.live.participants} in studio
            </Badge>
          )}
        </div>
      </div>
      <Link to={`/studios/${studio.id}`} className="truncate font-medium after:absolute after:inset-0">
        {studio.name}
      </Link>
      <p className="mt-1 text-sm text-muted">
        {plural(studio.recordingCount, 'recording')} · created {formatRelative(studio.createdAt)}
      </p>
      {/* sits above the stretched link */}
      <div className="relative mt-5 flex gap-2">
        <LinkButton to={`/studios/${studio.id}/live`} variant="primary" size="sm">
          Enter studio
        </LinkButton>
        <LinkButton to={`/studios/${studio.id}`} variant="ghost" size="sm">
          Details
        </LinkButton>
      </div>
    </div>
  )
}
